import type { ColumnMetadata } from '../grid';
import { normalizeGridKey } from '../grid/utils';
import type { FieldOccurrence, LayoutBlock, FieldSection } from './types';

export type FieldOccurrenceResolver = (
  sectionKey: string,
  indexInSection: number
) => ColumnMetadata | undefined;

const isViewable = (metadata: ColumnMetadata): boolean => metadata.canView !== false;

export const getFieldOccurrences = (section: FieldSection): FieldOccurrence[] => {
  if (section.fieldOccurrences) {
    return section.fieldOccurrences.filter(
      (occurrence) => Boolean(occurrence?.fieldName) || Boolean(occurrence?.fieldKey)
    );
  }

  const fieldNames = section.fieldNames ?? [];
  const fieldKeys = section.fieldKeys ?? [];
  const count = Math.max(fieldNames.length, fieldKeys.length);

  return Array.from({ length: count }, (_, index) => ({
    fieldName: fieldNames[index] ?? null,
    fieldKey: fieldKeys[index] ?? null,
  })).filter((occurrence) => Boolean(occurrence.fieldName) || Boolean(occurrence.fieldKey));
};

export const resolveFieldOccurrenceMetadata = (
  occurrence: FieldOccurrence,
  fields: ColumnMetadata[]
): ColumnMetadata | undefined => {
  const normalizedOccurrenceKey = normalizeGridKey(occurrence.fieldKey);
  const metadataByKey = normalizedOccurrenceKey
    ? fields.find((field) => normalizeGridKey(field.key) === normalizedOccurrenceKey)
    : undefined;

  return (
    metadataByKey ??
    (occurrence.fieldName
      ? fields.find((field) => field.dataField === occurrence.fieldName)
      : undefined)
  );
};

export function buildFieldOccurrenceResolver(
  blocks: LayoutBlock[] | undefined,
  fields: ColumnMetadata[]
): FieldOccurrenceResolver {
  const resolved = new Map<string, ColumnMetadata>();

  const visitSection = (section: FieldSection) => {
    getFieldOccurrences(section).forEach((occurrence, idx) => {
      const field = resolveFieldOccurrenceMetadata(occurrence, fields);
      if (field && isViewable(field)) {
        resolved.set(`${section.key}:${idx}`, field);
      }
    });
  };

  const visitBlock = (block: LayoutBlock) => {
    (block.fieldSections ?? []).forEach(visitSection);
    (block.children ?? []).forEach(visitBlock);
  };

  (blocks ?? []).forEach(visitBlock);

  return (sectionKey, indexInSection) => resolved.get(`${sectionKey}:${indexInSection}`);
}
